import type { CSSProperties, FC, ReactNode } from 'react'

import { useRef } from 'react'

import { cn } from '@/lib/utils'
import { gsap, useGsap, prefersReducedMotion } from '@/lib/gsap'

interface HoverMediaProps {
    src: string
    alt: string
    className?: string
    aspect?: number
    /* Parallax travel in percent as the card crosses the viewport. */
    parallax?: number
    rounded?: string
    style?: CSSProperties
}

/* Card media exactly as the reference builds it: two stacked copies of the
   same asset. The base layer softens and pushes back, a sharp duplicate wipes
   up over it, and the whole frame drifts on scroll. */
export const HoverMedia: FC<HoverMediaProps> = ({
    src,
    alt,
    className,
    aspect,
    parallax = 6,
    rounded = 'rounded-[var(--radius-card)]',
    style
}): ReactNode => {
    const scope = useRef<HTMLDivElement>(null)

    useGsap(() => {
        const root = scope.current

        if (!root || prefersReducedMotion() || parallax === 0) {
            return
        }

        gsap.fromTo(root.querySelectorAll('[data-hover-layer]'),
            { yPercent: -parallax },
            {
                yPercent: parallax,
                ease: 'none',
                scrollTrigger: {
                    trigger: root,
                    start: 'top bottom',
                    end: 'bottom top',
                    scrub: 0.6
                }
            }
        )
    }, scope, [parallax])

    return (
        <div
            ref={scope}
            className={cn('group/media relative overflow-hidden bg-ink-soft', rounded, className)}
            style={{ aspectRatio: aspect ? String(aspect) : undefined, ...style }}
        >
            <img
                data-hover-layer
                src={src}
                alt={alt}
                loading='lazy'
                decoding='async'
                className='absolute inset-0 size-full scale-[1.14] object-cover transition-[filter,transform] duration-[900ms] ease-[cubic-bezier(0.16,1,0.3,1)] group-hover/media:scale-[1.2] group-hover/media:blur-[6px]'
            />

            <img
                data-hover-layer
                src={src}
                alt=''
                aria-hidden='true'
                loading='lazy'
                decoding='async'
                className='absolute inset-0 size-full scale-[1.14] object-cover opacity-0 transition-[opacity,transform,clip-path] duration-[900ms] ease-[cubic-bezier(0.16,1,0.3,1)] [clip-path:inset(100%_0_0_0)] group-hover/media:scale-[1.06] group-hover/media:opacity-100 group-hover/media:[clip-path:inset(0%_0_0_0)]'
            />
        </div>
    )
}
